// chatbot-frontend\client\src\components\FormEditor.js

import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
// import "./FormEditor.css";
import { v4 as uuidv4 } from "uuid"; // Vik added May 1, 2025 for unique formId
import {
  TextField,
  Select,
  FormControl,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Checkbox,
  FormHelperText,
  Stack,
  Box,
  FormControlLabel,
  Modal,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close"; // Vik added April 25, 2025 for modal close button
import Dialogue from "./Dialogue";

const FormEditor = ({
  data,
  formState,
  onSave,
  metadata,
  foregroundColor,
  backgroundColor,
  backgroundColorLight,
  userId,
  chatUserEmail,
  chatUserName,
  apiVersion,
  agentId,
  showInModal = false, // Vik added April 25, 2025 for modal support
  scrollToBottom,
  formSubmissionResponse,
}) => {
  console.log("FormEditor data:", data);
  // console.log("formSubmissionResponse :", formSubmissionResponse);

  const configObj = data?.configObj;
  const apiURL = data?.configObj?.apiUrl || null;
  const [formSaved, setFormSaved] = useState(formState);
  const [isModalOpen, setIsModalOpen] = useState(showInModal);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formId, setFormId] = useState("");
  const [creationDate, setCreationDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    data?.configObj?.successMessage || null
  );

  // Generate unique formId when component mounts
  useEffect(() => {
    const uniqueFormId = uuidv4();
    const currentDate = new Date().toISOString(); // ISO 8601 format (e.g., "2025-05-01T12:34:56.789Z")
    setFormId(uniqueFormId);
    setCreationDate(currentDate);
  }, []); // Empty dependency array ensures this runs once on mount

  // Build validation schema based on configObj.fields
  const urlPrefix = "http://localhost:8888";
  const validationSchema = Yup.object().shape(
    configObj.fields.reduce((acc, field) => {
      if (field.required && field.input_type !== "display") {
        if (field.input_type === "email") {
          acc[field.name] = Yup.string()
            .email("Invalid email format")
            .required(`${field.display_name} is required`);
        } else if (field.input_type === "number") {
          acc[field.name] = Yup.number()
            .typeError(`${field.display_name} must be a number`)
            .required(`${field.display_name} is required`);
        } else if (field.input_type === "checkbox") {
          acc[field.name] = Yup.boolean()
            .required(`${field.display_name} is required`)
            .oneOf([true], `${field.display_name} must be checked`);
        } else {
          acc[field.name] = Yup.string().required(
            `${field.display_name} is required`
          );
        }
      }
      return acc;
    }, {})
  );

  // Initialize Formik
  const formik = useFormik({
    initialValues: configObj.fields.reduce((acc, field) => {
      /* Vik modified April 25, 2025 to autopopulate email and name if available */
      if (field.name === "email" && chatUserEmail) {
        acc[field.name] = chatUserEmail;
      } else if (field.name === "full_name" && chatUserName) {
        acc[field.name] = chatUserName;
      } else {
        /* Vik modified May 2, 2025 to respect default value for all field types */
        acc[field.name] =
          field.input_type === "checkbox"
            ? field.default ?? false
            : field.default ?? "";
      }
      return acc;
    }, {}),
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true); // Vik added May 1, 2025 to show loading spinner
      setErrorMessage(""); // Vik added May 1, 2025 to clear previous errors
      console.log("Form data:", JSON.stringify(values, null, 2));
      const url = urlPrefix + "/api" + apiVersion + "/submitForm";
      try {
        let response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json; charset=UTF-8",
            Accept: "application/json",
          },
          body: JSON.stringify({
            formId: formId, // Vik added May 1, 2025 to include formId in payload
            userId: userId,
            email: chatUserEmail || values.email || "",
            userName: chatUserName || values.full_name || "",
            agentId: agentId,
            apiURL: apiURL,
            formData: values,
            creationDate: creationDate,
            data: values,
            formName: configObj.formName, // Vik (May 2, 2025) To identify the action
            // formName: "contact_googlesheet",
          }),
        });
        const result = await response.json();
        if (result.success) {
          //onSave(formData); // Trigger saving the form data
          setFormSaved(true);
          if (showInModal) {
            setIsModalOpen(false); // Vik added April 25, 2025 to close modal on successful submission
          }
        } else {
          // Vik added May 1, 2025 to handle API failure
          setErrorMessage(
            result.message || "Failed to submit the form. Please try again."
          );
          console.error("Form submission failed:", result);
        }
      } catch (error) {
        // Vik added May 1, 2025 to handle network or other errors
        setErrorMessage(
          "An error occurred while submitting the form. Please try again."
        );
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false); // Vik added May 1, 2025 to hide loading spinner
      }
      if (true) {
        // This block is preserved for future use
      } else {
        const dataToSend = { question: values };
        let response = await fetch(configObj.webhook, {
          method: "POST",
          headers: {
            //Authorization: "Bearer " + token,
            "Content-Type": "application/json; charset=UTF-8",
            Accept: "application/json",
          },
          body: JSON.stringify(dataToSend),
        });
        const data = response.json();
        console.log(JSON.stringify(data));
      }
    },
  });

  const {
    header = {},
    content = {},
    border = {},
    headerBackgroundColor = "#ccc",
    rowAlternateColor = true,
    alternateRowColors = [backgroundColor, backgroundColorLight],
    borderRadius = "12px",
  } = metadata;

  const formStyle = {
    width: "100%",
    borderCollapse: "collapse",
    border: `${border?.width || "3px"} solid ${
      border?.color || foregroundColor
    }`,
    borderRadius: borderRadius,
    overflow: "hidden",
    backgroundColor: backgroundColor,
    color: foregroundColor,
  };
  const inputStyle = {
    width: "19rem",
    borderCollapse: "collapse",
    border: `${border?.width || "1px"} solid ${
      border?.color || foregroundColor
    }`,
    borderRadius: borderRadius,
    overflow: "hidden",
    backgroundColor: backgroundColor,
    color: foregroundColor,
    borderColor: foregroundColor,
  };

  const modalStyle = {
    // Vik added April 25, 2025 for modal styling
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    overflowY: "auto",
    overflowX: "hidden",
    width: "85%",
    maxHeight: "80dvh",
    bgcolor: "white",
    border: `2px solid ${foregroundColor}`,
    borderRadius: "20px",
    boxShadow: 24,
    p: 2,
    display: "flex",
    flexDirection: "column",
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate all fields
    const errors = await formik.validateForm();
    console.log("Validation errors:", errors); // Debug: Check which fields have errors
    // Mark all fields as touched to show errors
    const touched = configObj.fields.reduce((acc, field) => {
      acc[field.name] = true;
      return acc;
    }, {});
    await formik.setTouched(touched, true); // Set touched and validate
    await formik.setErrors(errors); // Ensure errors are set
    if (Object.keys(errors).length > 0) {
      console.log("Stopping submission due to errors"); // Debug: Confirm errors detected
      return; // Stop submission if there are errors
    }
    console.log("Proceeding with submission"); // Debug: Confirm submission
    formik.handleSubmit();
  };

  const handleModalClose = () => {
    // Vik added April 25, 2025 to handle modal close
    setIsModalOpen(false);
    setErrorMessage(""); // Vik added May 1, 2025 to clear error on modal close
  };

  // console.log("FormEditor geoData:", geoData); // Vik added May 1, 2025 to debug formSaved state

  const renderForm = () => (
    // Vik added April 25, 2025 to encapsulate form rendering
    <div style={{ formStyle }}>
      <form onSubmit={handleSubmit} noValidate>
        <h2 style={{ color: foregroundColor }}>{configObj.name}</h2>
        {configObj.fields.map((field) => {
          if (field.input_type === "display") {
            return (
              <div key={field.name} style={{ marginBottom: "1em" }}>
                <label style={{ color: foregroundColor }}>
                  {field.display_name}
                </label>
              </div>
            );
          }

          if (field.input_type === "dropdown") {
            return (
              <div
                style={{
                  color: foregroundColor,
                  borderRadius: borderRadius,
                }}
                key={field.name}
              >
                <FormControl
                  variant="outlined"
                  sx={{
                    minWidth: 120,
                    color: foregroundColor,
                    backgroundColor: backgroundColor,
                    borderRadius: borderRadius,
                    marginBottom: "1em",
                  }}
                  required
                  error={
                    formik.touched[field.name] && !!formik.errors[field.name]
                  }
                >
                  <InputLabel id="demo-simple-select-required-label">
                    {field.display_name}
                  </InputLabel>
                  <Select
                    className="form-group"
                    labelId="demo-simple-select-label"
                    sx={{
                      // ALTERNATIVE 1
                      "& .MuiSvgIcon-root": {
                        color: foregroundColor, // arrow-svg-color
                      },
                      "& .MuiFormLabel-root": {
                        color: foregroundColor, // Default label color
                      },
                      "& .MuiFormLabel-root.Mui-focused": {
                        color: foregroundColor, // Default focused label color
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: foregroundColor, // outline-color by default
                        borderRadius: borderRadius,
                      },
                      "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: foregroundColor, // outline-color on hover
                          borderRadius: borderRadius,
                        },
                      "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: foregroundColor, // outline-color on focus
                          borderRadius: borderRadius,
                        },
                      backgroundColor: backgroundColor,
                      textAlign: "left",
                    }}
                    MenuProps={{
                      // Add this to style the menu
                      PaperProps: {
                        style: {
                          backgroundColor: backgroundColorLight, // Background color of the dropdown menu
                          color: foregroundColor, // Text color of the dropdown menu
                        },
                      },
                    }}
                    name={field.name}
                    style={{
                      borderColor: foregroundColor,
                      color: foregroundColor,
                      width: "19rem",
                      backgroundColor: backgroundColor,
                    }}
                    placeholder={field.placeholder}
                    value={formik.values[field.name]}
                    required={field.required}
                    label={field.display_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    {field.values.map((option, idx) => (
                      <MenuItem
                        key={idx}
                        value={option}
                        sx={{
                          backgroundColor: backgroundColor,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched[field.name] && formik.errors[field.name] && (
                    <FormHelperText>{formik.errors[field.name]}</FormHelperText>
                  )}
                </FormControl>
              </div>
            );
          }

          if (field.input_type === "checkbox") {
            return (
              <div
                style={{ color: foregroundColor, marginBottom: "1em" }}
                key={field.name}
              >
                <FormControl
                  error={
                    formik.touched[field.name] && !!formik.errors[field.name]
                  }
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <FormControlLabel
                      required
                      control={
                        <Checkbox
                          sx={{
                            color: foregroundColor,
                            "&.Mui-error": {
                              color: "#D63B3B", // Red color when error
                            },
                            "& .MuiSvgIcon-root": {
                              border:
                                formik.touched[field.name] &&
                                formik.errors[field.name]
                                  ? "1px solid #D63B3B" // Red border on error
                                  : "",
                            },
                          }}
                          style={{
                            borderColor: foregroundColor,
                            color: foregroundColor,
                          }}
                          name={field.name}
                          checked={formik.values[field.name]}
                          onChange={(e) =>
                            formik.setFieldValue(field.name, e.target.checked)
                          }
                          onBlur={formik.handleBlur}
                        />
                      }
                      label={field.display_name}
                    />
                  </div>
                  {formik.touched[field.name] && formik.errors[field.name] && (
                    <FormHelperText style={{ color: "#D63B3B" }}>
                      {formik.errors[field.name]}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            );
          }

          return (
            <div
              key={field.name}
              style={{
                display: "flex",
                flexDirection: "column",
                marginBottom: "1rem",
              }}
            >
              {field.input_type === "textarea" ? (
                <div style={{ inputStyle }}>
                  <TextField
                    variant="outlined"
                    sx={{
                      // Input text color
                      "& .MuiInputBase-input": {
                        color: foregroundColor,
                      },
                      // Label color (when not focused)
                      "& .MuiInputLabel-root": {
                        color: foregroundColor,
                      },
                      // Label color (when focused)
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: foregroundColor,
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: borderRadius, // Added for rounded corners
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: foregroundColor,
                          borderRadius: borderRadius, // Ensure the outline also has rounded corners
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: foregroundColor,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: foregroundColor,
                        },
                      },
                      // Bottom border color (when not focused)
                      "& .MuiOutlinedInput-root .Mui-notchedOutline": {
                        borderColor: foregroundColor,
                        borderWidth: "5px",
                      },
                      // Bottom border color (when focused)
                      "& .MuiOutlinedInput-root.Mui-focused .Mui-notchedOutline":
                        {
                          borderColor: foregroundColor,
                          borderWidth: "5px",
                        },
                      // Bottom border color (when hovered)
                      "& .MuiOutlinedInput-root:hover .Mui-notchedOutline": {
                        borderColor: foregroundColor,
                        borderWidth: "5px",
                      },
                    }}
                    InputProps={{
                      style: {
                        color: foregroundColor, // overrides the input color
                      },
                    }}
                    InputLabelProps={{
                      style: {
                        color: foregroundColor, // overrides label color.
                      },
                    }}
                    placeholder={field.placeholder}
                    name={field.name}
                    value={formik.values[field.name]}
                    label={field.display_name}
                    required={field.required}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={4}
                    fullWidth
                    multiline
                    style={{
                      borderColor: foregroundColor,
                      color: foregroundColor,
                      width: "19rem",
                    }}
                    error={
                      formik.touched[field.name] && !!formik.errors[field.name]
                    }
                    helperText={
                      formik.touched[field.name] && formik.errors[field.name]
                    }
                  />
                </div>
              ) : (
                <div style={{ inputStyle }}>
                  <TextField
                    variant="outlined"
                    sx={{
                      // Input text color
                      "& .MuiInputBase-input": {
                        color: foregroundColor,
                      },
                      // Label color (when not focused)
                      "& .MuiInputLabel-root": {
                        color: foregroundColor,
                      },
                      // Label color (when focused)
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: foregroundColor,
                      },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: borderRadius, // Added for rounded corners
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: foregroundColor,
                          borderRadius: borderRadius, // Ensure the outline also has rounded corners
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: foregroundColor,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: foregroundColor,
                        },
                      },
                      // Bottom border color (when not focused)
                      "& .MuiOutlinedInput-root .Mui-notchedOutline": {
                        borderColor: foregroundColor,
                        borderWidth: "5px",
                      },
                      // Bottom border color (when focused)
                      "& .MuiOutlinedInput-root.Mui-focused .Mui-notchedOutline":
                        {
                          borderColor: foregroundColor,
                        },
                      // Bottom border color (when hovered)
                      "& .MuiOutlinedInput-root:hover .Mui-notchedOutline": {
                        borderColor: foregroundColor,
                      },
                    }}
                    InputProps={{
                      style: {
                        color: foregroundColor, // overrides the input color
                      },
                    }}
                    InputLabelProps={{
                      style: {
                        color: foregroundColor, // overrides label color.
                      },
                    }}
                    type={field.input_type}
                    placeholder={field.placeholder}
                    label={field.display_name}
                    name={field.name}
                    value={formik.values[field.name]}
                    required={field.required}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{ color: foregroundColor, width: "19rem" }}
                    error={
                      formik.touched[field.name] && !!formik.errors[field.name]
                    }
                    helperText={
                      formik.touched[field.name] && formik.errors[field.name]
                    }
                  />
                </div>
              )}
            </div>
          );
        })}
        {errorMessage && ( // Vik added May 1, 2025 to display error message
          <Alert severity="error" sx={{ marginBottom: "1rem" }}>
            {errorMessage}
          </Alert>
        )}
        <Button
          variant="contained" // Material-UI variant, can be "outlined", "text", etc.
          color="primary" // Button color
          size="medium"
          type="submit"
          disabled={isSubmitting}
          sx={{
            minWidth: "7rem",
            fontFamily: "Google Sans", // Consistent with your app’s typography
            textTransform: "none", // Avoid uppercase text
            borderRadius: borderRadius,
          }}
        >
          {isSubmitting ? (
            <CircularProgress
              style={{ color: foregroundColor }}
              size="1.5rem"
            />
          ) : (
            configObj.buttonName
          )}
        </Button>
      </form>
    </div>
  );

  useEffect(() => {
    if (scrollToBottom) {
      scrollToBottom();
    }
  });

  return (
    <>
      {formSaved === false ? (
        showInModal ? (
          // Vik added April 25, 2025 for modal rendering
          <Modal
            open={isModalOpen}
            onClose={handleModalClose}
            aria-labelledby="form-modal-title"
            aria-describedby="form-modal-description"
          >
            <Box sx={modalStyle}>
              <Box display="flex" justifyContent="flex-end">
                {/* <IconButton onClick={handleModalClose}>
                  <CloseIcon />
                </IconButton> */}
              </Box>
              {renderForm()}
            </Box>
          </Modal>
        ) : (
          renderForm()
        )
      ) : (
        <Box
          sx={{
            width: "inherit",
            height: "inherit",
            marginTop: "15px",
          }}
        >
          <Stack width="100%" alignItems={"flex-start"}>
            <Dialogue
              style={{
                overflow: "visible",
                boxSizing: "border-box",
                background: "#EDEDED",
                borderRadius: "0px 20px 20px 20px",
                padding: "10px 16px",
                gap: "10px",
                maxWidth: "85%",
                fontSize: "16px",
              }}
            >
              <pre
                style={{
                  wordBreak: "break-word", // Break words at any point
                  whiteSpace: "pre-wrap", // Preserve whitespace and allow wrapping
                  width: "100%", // Ensure the container width is respected
                  fontWeight: "lighter",
                  fontFamily: "inherit",
                  margin: 0,
                  color: "#292929",
                }}
              >
                {successMessage ||
                  formSubmissionResponse ||
                  `Thank you for your submitting your question. We will be back to
                you shortly.`}
              </pre>
            </Dialogue>
          </Stack>
        </Box>
      )}
    </>
  );
};

export default FormEditor;

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

/* Structure of data props to be passed into FormEditot component form index.js. Vik, Apr 25, 2025 */

// const sampleData = {
//   configObj: {
//     id: "form_12345",
//     name: "Contact Form",
//     buttonName: "Submit",
//     apiUrl: "https://dummyapi.free.beeceptor.com/formTest",
//     apiUrl: "https://hook.us2.make.com/q46fruxxusiibmxu6m6aiyarga2y751r",
//     fields: [
//       {
//         name: "full_name",
//         display_name: "Full Name",
//         input_type: "text",
//         required: true,
//         default: "",
//         placeholder: "Enter your full name",
//       },
//       {
//         name: "email",
//         display_name: "Email Address",
//         input_type: "email",
//         required: true,
//         default: "",
//         placeholder: "Enter your email",
//       },
//       {
//         name: "phone",
//         display_name: "Phone Number",
//         input_type: "number",
//         required: true,
//         default: "",
//         placeholder: "Enter your phone number",
//       },
//       {
//         name: "department",
//         display_name: "Department",
//         input_type: "dropdown",
//         required: true,
//         default: "",
//         values: ["Sales", "Support", "Marketing", "Other"],
//       },
//       {
//         name: "agree_terms",
//         display_name: "Agree to Terms",
//         input_type: "checkbox",
//         required: true,
//         default: false,
//       },
//       {
//         name: "message",
//         display_name: "Your Message",
//         input_type: "textarea",
//         required: true,
//         default: "",
//         placeholder: "Type your message here",
//       },
//       {
//         name: "info",
//         display_name: "Information",
//         input_type: "display",
//       },
//     ],
//   },
//   user: "user_98765", // Could be an object or string, depending on backend
//   agent: {
//     allowedURLs: allowedURLs, // URLs allowed for CORS
//   },
// };
